import { Gift, type LucideIcon } from 'lucide-react';
import { useState, type CSSProperties } from 'react';
import { cssVar } from '../../tokens/cssVar';
import type { AvatarColor, OccasionKind } from '../../tokens/avatar';
import { cx } from '../../utils/cx';
import { Icon } from '../Icon/Icon';
import s from './Thumbnail.module.css';

export type ThumbnailProps = {
  src?: string | null;
  /** Alt text. Empty (the default) when the item title is shown next to it. */
  alt?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Placeholder color when there's no image (or it fails to load). */
  tone?: { kind: OccasionKind } | { avatar: AvatarColor };
  /** Placeholder glyph: an icon or an emoji. */
  placeholder?: LucideIcon | string;
  className?: string;
};

/**
 * Product image with a friendly colored fallback. Hotlinked preview images load lazily with
 * no referrer (doc 10); a broken link quietly shows the placeholder.
 */
export function Thumbnail({
  src,
  alt = '',
  size = 'sm',
  tone = { kind: 'other' },
  placeholder = Gift,
  className,
}: ThumbnailProps) {
  const [broken, setBroken] = useState(false);
  const bg = 'kind' in tone ? cssVar(`kind.${tone.kind}.bg`) : cssVar(`avatar.${tone.avatar}.bg`);
  const fg = 'kind' in tone ? cssVar(`kind.${tone.kind}.fg`) : cssVar(`avatar.${tone.avatar}.fg`);
  const showImage = src && !broken;
  return (
    <span
      className={cx(s.thumb, className)}
      data-size={size}
      style={showImage ? undefined : ({ '--bg': bg, '--fg': fg } as CSSProperties)}
      data-placeholder={showImage ? undefined : true}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
        />
      ) : typeof placeholder === 'string' ? (
        <span aria-hidden="true">{placeholder}</span>
      ) : (
        <Icon icon={placeholder} size={size === 'sm' ? 'md' : 'lg'} />
      )}
    </span>
  );
}
